import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState<'request' | 'confirm'>('request');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotPassword, setForgotPassword] = useState('');
  const [forgotPasswordConfirm, setForgotPasswordConfirm] = useState('');
  const [firstAccessOpen, setFirstAccessOpen] = useState(false);
  const [firstAccessSession, setFirstAccessSession] = useState('');
  const [firstAccessPassword, setFirstAccessPassword] = useState('');
  const [firstAccessConfirm, setFirstAccessConfirm] = useState('');
  const [firstAccessLoading, setFirstAccessLoading] = useState(false);
  
  const { login, completeFirstAccess, authStrategy, forgotPassword: requestPasswordReset, confirmForgotPassword } = useAuth();
  const navigate = useNavigate();
  const isInsecureCognitoPage =
    authStrategy === 'cognito' &&
    window.location.protocol !== 'https:' &&
    window.location.hostname !== 'localhost';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/');
    } else if (result.challenge === 'NEW_PASSWORD_REQUIRED' && result.session) {
      setEmail(result.email || email);
      setFirstAccessSession(result.session);
      setFirstAccessPassword('');
      setFirstAccessConfirm('');
      setFirstAccessOpen(true);
    } else {
      setError(result.message || 'Erro desconhecido');
    }
    
    setLoading(false);
  };

  const handleFirstAccess = async () => {
    if (firstAccessPassword !== firstAccessConfirm) {
      message.error('A confirmação não confere com a nova senha.');
      return;
    }
    if (firstAccessPassword.length < 12) {
      message.error('A nova senha deve ter no mínimo 12 caracteres.');
      return;
    }
    setFirstAccessLoading(true);
    const result = await completeFirstAccess(email, firstAccessPassword, firstAccessSession);
    setFirstAccessLoading(false);
    if (!result.success) {
      message.error(result.message);
      return;
    }
    setFirstAccessOpen(false);
    message.success('Primeiro acesso concluído.');
    navigate('/');
  };

  const openForgotPassword = () => {
    setForgotEmail(email);
    setForgotCode('');
    setForgotPassword('');
    setForgotPasswordConfirm('');
    setForgotStep('request');
    setForgotOpen(true);
  };

  const handleForgotRequest = async () => {
    if (!forgotEmail) {
      message.error('Informe seu e-mail corporativo.');
      return;
    }

    setForgotLoading(true);
    const result = await requestPasswordReset(forgotEmail);
    setForgotLoading(false);

    if (!result.success) {
      message.error(result.message || 'Não foi possível solicitar a recuperação.');
      return;
    }

    message.success(result.message || 'Código enviado para o e-mail cadastrado.');
    setForgotStep('confirm');
  };

  const handleForgotConfirm = async () => {
    if (!forgotEmail || !forgotCode || !forgotPassword || !forgotPasswordConfirm) {
      message.error('Preencha e-mail, código e nova senha.');
      return;
    }

    if (forgotPassword !== forgotPasswordConfirm) {
      message.error('A confirmação não confere com a nova senha.');
      return;
    }

    if (forgotPassword.length < 12) {
      message.error('A nova senha deve ter no mínimo 12 caracteres.');
      return;
    }

    setForgotLoading(true);
    const result = await confirmForgotPassword(forgotEmail, forgotCode, forgotPassword);
    setForgotLoading(false);

    if (!result.success) {
      message.error(result.message || 'Não foi possível redefinir a senha.');
      return;
    }

    message.success(result.message || 'Senha redefinida com sucesso.');
    setEmail(forgotEmail);
    setPassword('');
    setForgotOpen(false);
  };

  return (
    <main className="login-page">
      <section className="login-shell" aria-label="Login FinOps">
        <div className="login-brand-panel">
          <div className="login-brand-mark">
            <img src="/assets/incloud-logo.svg" alt="inCloud Tecnologia" />
          </div>

          <div className="login-hero-copy">
            <p className="login-kicker">xKnow FinOps Cloud</p>
            <h1>IA proprietária para reduzir custos Cloud</h1>
            <p>
              Centralize AWS, Azure e Google Cloud em uma visão FinOps executiva.
              A xKnow combina previsão, anomalias, causa raiz, rightsizing e
              aprendizado contínuo para priorizar economia real.
            </p>
            <div className="login-ai-proof">
              Hybrid AI System · Ensemble Predictor · Intelligent Rightsizing
            </div>
          </div>

          <div className="login-signal-grid" aria-hidden="true">
            <div>
              <span>AWS</span>
              <strong>AssumeRole</strong>
            </div>
            <div>
              <span>Azure + GCP</span>
              <strong>Multi-Cloud</strong>
            </div>
            <div>
              <span>IA própria</span>
              <strong>12+ módulos</strong>
            </div>
          </div>
        </div>

        <div className="login-card-panel">
          <form onSubmit={handleSubmit} className="login-card">
            <div className="login-mobile-logo">
              <img src="/assets/incloud-logo.svg" alt="inCloud Tecnologia" />
            </div>

            <div className="login-card-heading">
              <p>Acesso ao painel</p>
              <h2>Entrar na plataforma</h2>
            </div>

            {error && (
              <div className="login-error" role="alert">
                {error}
              </div>
            )}

          <div className="login-field">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="admin@incloud.com.br"
              autoComplete="email"
            />
          </div>

          <div className="login-field">
            <label>Senha</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Sua senha"
              autoComplete="current-password"
            />
          </div>

          {authStrategy === 'cognito' && (
            <button
              type="button"
              className="login-forgot-button"
              onClick={openForgotPassword}
            >
              Esqueci minha senha
            </button>
          )}

          {authStrategy === 'cognito' && (
            <div className="login-auth-summary" aria-live="polite">
              <span />
              <p>
                {isInsecureCognitoPage
                  ? 'Para entrar, acesse a URL segura HTTPS do ambiente.'
                  : 'Acesso protegido com autenticação segura.'}
              </p>
            </div>
          )}

          {isInsecureCognitoPage && (
            <a className="login-secure-link" href="https://d2td37b66hzwfg.cloudfront.net">
              Abrir ambiente seguro
            </a>
          )}

          <button
            type="submit"
            disabled={loading || isInsecureCognitoPage}
            className="login-submit"
          >
            <span>{loading ? 'Entrando...' : 'Entrar'}</span>
          </button>

          <p className="login-support">
            Ambiente controlado da xKnow FinOps Cloud.
          </p>
        </form>
      </div>
      </section>

      <Modal
        title="Concluir primeiro acesso"
        open={firstAccessOpen}
        okText="Criar senha e entrar"
        cancelText="Cancelar"
        confirmLoading={firstAccessLoading}
        onOk={handleFirstAccess}
        onCancel={() => setFirstAccessOpen(false)}
      >
        <p>Crie uma senha permanente para concluir seu primeiro acesso.</p>
        <div className="login-forgot-modal">
          <label>Nova senha</label>
          <input type="password" value={firstAccessPassword} onChange={e => setFirstAccessPassword(e.target.value)} autoComplete="new-password" />
          <label>Confirmar nova senha</label>
          <input type="password" value={firstAccessConfirm} onChange={e => setFirstAccessConfirm(e.target.value)} autoComplete="new-password" />
          <small>Mínimo de 12 caracteres, com maiúscula, minúscula e número.</small>
        </div>
      </Modal>

      <Modal
        title="Recuperar senha"
        open={forgotOpen}
        okText={forgotStep === 'request' ? 'Enviar código' : 'Redefinir senha'}
        cancelText="Cancelar"
        confirmLoading={forgotLoading}
        onOk={forgotStep === 'request' ? handleForgotRequest : handleForgotConfirm}
        onCancel={() => setForgotOpen(false)}
      >
        <div className="login-forgot-modal">
          <p>
            {forgotStep === 'request'
              ? 'Informe seu e-mail cadastrado. Enviaremos um código de verificação pelo Cognito.'
              : 'Digite o código recebido por e-mail e escolha uma nova senha.'}
          </p>

          <label>
            <span>E-mail corporativo</span>
            <input
              type="email"
              value={forgotEmail}
              onChange={(event) => setForgotEmail(event.target.value)}
              placeholder="voce@empresa.com"
              autoComplete="email"
            />
          </label>

          {forgotStep === 'confirm' && (
            <>
              <label>
                <span>Código recebido por e-mail</span>
                <input
                  type="text"
                  value={forgotCode}
                  onChange={(event) => setForgotCode(event.target.value)}
                  placeholder="123456"
                  autoComplete="one-time-code"
                />
              </label>

              <label>
                <span>Nova senha</span>
                <input
                  type="password"
                  value={forgotPassword}
                  onChange={(event) => setForgotPassword(event.target.value)}
                  placeholder="Mínimo de 12 caracteres"
                  autoComplete="new-password"
                />
              </label>

              <label>
                <span>Confirmar nova senha</span>
                <input
                  type="password"
                  value={forgotPasswordConfirm}
                  onChange={(event) => setForgotPasswordConfirm(event.target.value)}
                  placeholder="Repita a nova senha"
                  autoComplete="new-password"
                />
              </label>

              <button
                type="button"
                className="login-forgot-link"
                onClick={handleForgotRequest}
                disabled={forgotLoading}
              >
                Reenviar código
              </button>
            </>
          )}
        </div>
      </Modal>
    </main>
  );
};

export default Login;
